/**
 * DeveloperBreakdownSection — View E (RAISE-16867 D7/D9).
 *
 * Groups events by developer. Today, developer attribution is absent in all
 * payloads, so every event lands in an "Unattributed" bucket. The component
 * renders the table plus a persistent info notice explaining the degraded state.
 *
 * Plan-gate notice on 403. Sortable by cost, stories, avg.
 */

import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Info, ChevronUp, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  aggregateByActor,
  type StoryCostEvent,
  type ActorAggregate,
} from "@/lib/cost/storyCostEvents";
import { formatCost } from "@/lib/format";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeveloperBreakdownSectionProps {
  events: StoryCostEvent[];
  loading: boolean;
  error: string | null;
  planGated: boolean;
  truncated: boolean;
  total: number;
}

type SortKey = "costUsd" | "stories" | "avgPerStory";
type SortDir = "asc" | "desc";

// ---------------------------------------------------------------------------
// Sort helper
// ---------------------------------------------------------------------------

function sortActors(
  rows: ActorAggregate[],
  key: SortKey,
  dir: SortDir,
): ActorAggregate[] {
  return [...rows].sort((a, b) => {
    const cmp = a[key] - b[key];
    return dir === "asc" ? cmp : -cmp;
  });
}

// ---------------------------------------------------------------------------
// Column header with sort toggle
// ---------------------------------------------------------------------------

interface SortHeaderProps {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  currentDir: SortDir;
  onSort: (key: SortKey) => void;
}

function SortHeader({ label, sortKey, currentKey, currentDir, onSort }: SortHeaderProps) {
  const active = currentKey === sortKey;
  return (
    <TableHead
      className="cursor-pointer select-none"
      onClick={() => onSort(sortKey)}
    >
      <span className="flex items-center gap-1">
        {label}
        {active && (
          currentDir === "asc" ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        )}
      </span>
    </TableHead>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function DeveloperBreakdownSection({
  events,
  loading,
  error,
  planGated,
  truncated: _truncated,
  total: _total,
}: DeveloperBreakdownSectionProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>("costUsd");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  const actorRows = useMemo(() => {
    const aggregated = aggregateByActor(events);
    return sortActors(aggregated, sortKey, sortDir);
  }, [events, sortKey, sortDir]);

  // Feature-detect: are all events unattributed?
  const allUnattributed = actorRows.every((r) => r.developer === "Unattributed");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">
          {t("costPage.developerBreakdown.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {planGated ? (
          <p className="text-sm text-muted-foreground">
            {t("costPage.developerBreakdown.planGate")}
          </p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : actorRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("costPage.developerBreakdown.empty")}
          </p>
        ) : (
          <>
            {/* Degraded-state notice: always shown while attribution is absent (D7) */}
            {allUnattributed && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <p>{t("costPage.developerBreakdown.unattributedNotice")}</p>
              </div>
            )}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("costPage.developerBreakdown.columnDeveloper")}
                  </TableHead>
                  <SortHeader
                    label={t("costPage.developerBreakdown.columnStories")}
                    sortKey="stories"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label={t("costPage.developerBreakdown.columnCost")}
                    sortKey="costUsd"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortHeader
                    label={t("costPage.developerBreakdown.columnAvg")}
                    sortKey="avgPerStory"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {actorRows.map((row) => (
                  <TableRow key={row.developer}>
                    <TableCell className="text-sm font-medium">
                      {row.developer}
                    </TableCell>
                    <TableCell className="text-xs">{row.stories}</TableCell>
                    <TableCell className="text-xs font-medium">
                      {formatCost(row.costUsd)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatCost(row.avgPerStory)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}
